
import React from 'react'
import { Link } from 'react-router-dom'
import {Heart, Shield, Globe, Users, ArrowRight, Handshake, Lock, Stethoscope} from 'lucide-react'

const About = () => {
  const principles = [
    {
      icon: <Handshake className="h-[26px] w-[26px] text-primary-teal" />,
      title: "Ethical Recruitment Only",
      description: "We partner only with recruiters who respect nurses' rights and provide transparent, verified opportunities."
    },
    {
      icon: <Lock className="h-[26px] w-[26px] text-primary-teal" />,
      title: "Data Privacy",
      description: "Your profile stays private until you choose to share it — you remain in control of your professional information."
    },
    {
      icon: <Globe className="h-[26px] w-[26px] text-primary-teal" />,
      title: "Multi-Country Flexibility",
      description: "Choose your preferred path — US, UK, GCC, or Canada — or prepare for multiple destinations at once."
    },
    {
      icon: <Users className="h-[26px] w-[26px] text-primary-teal" />,
      title: "Community Support",
      description: "Connect with fellow nurses preparing for international placements to share experiences, tips, and support."
    }
  ]

  return (
    <div className="min-h-screen py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-primary-navy mb-3">
            Built by clinicians for nurses
          </h1>
          <p className="text-xl text-primary-slate max-w-[920px] mx-auto mb-8">
            Our mission is to make global nursing careers accessible, ethical, and supportive for every nurse.
          </p>
        </div>

        {/* Story Section */}
        <div className="max-w-4xl mx-auto mb-20">
          <div className="bg-white rounded-2xl p-8 md:p-12 border border-gray-200">
            <h2 className="text-3xl font-bold text-primary-navy mb-8 text-center">Our Story</h2>
            
            <div className="space-y-6 text-lg text-primary-slate leading-relaxed">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary-teal rounded-full mt-3 mr-4 flex-shrink-0"></span>
                  <span>Founded by a healthcare professional based in the GCC, with deep access to nurse networks in Pakistan, India, and the Gulf.</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary-teal rounded-full mt-3 mr-4 flex-shrink-0"></span>
                  <span>We've personally experienced the challenges nurses face when pursuing international careers — from licensing hurdles to verification delays.</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary-teal rounded-full mt-3 mr-4 flex-shrink-0"></span>
                  <span>Nursing Hive was created to simplify this process and make it ethical, transparent, and empowering.</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary-teal rounded-full mt-3 mr-4 flex-shrink-0"></span>
                  <span>Profiles remain private until you choose to share with recruiters, giving you full control of your data and journey.</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary-teal rounded-full mt-3 mr-4 flex-shrink-0"></span>
                  <span>Every nurse deserves trustworthy guidance, verified pathways, and equal opportunity — regardless of geography.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Principles Section */}
        <div className="mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-navy text-center mb-16">
            Our Principles
          </h2>
          <div className="grid md:grid-cols-2 gap-[18px]">
            {principles.map((principle, index) => (
              <div key={index} className="bg-primary-light rounded-xl p-8 hover:shadow-lg transition-shadow min-h-[130px]">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {principle.icon}
                  </div>
                  <div>
                    <h3 className="text-[1.05rem] font-semibold text-primary-navy mb-3">
                      {principle.title}
                    </h3>
                    <p className="text-primary-slate text-[0.95rem]">
                      {principle.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Founder Note */}
        <div className="bg-gradient-to-r from-primary-light to-white rounded-2xl p-8 md:p-12 border border-primary-teal/20 mb-16">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <Stethoscope className="h-8 w-8 text-primary-teal" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-primary-navy mb-4">A note from the founder</h2>
              <p className="text-lg text-primary-slate mb-6 leading-relaxed">
                "International nursing mobility is complex — often filled with misinformation, delays, and unfair recruitment practices. 
                Nursing Hive was created to make the process transparent, ethical, and empowering — so every nurse can progress with confidence, dignity, and verified credentials."
              </p>
              <div className="border-t border-primary-teal/20 pt-4">
                <p className="font-semibold text-primary-navy">Dr. Muhammad Awais Amin</p>
                <p className="text-sm text-primary-slate">BSc, MBBS, CHPE, FCPS (ORL-HNS), FACS</p>
                <p className="text-sm text-primary-teal">Founder & Clinical Advisor, Nursing Hive</p>
              </div>
            </div>
          </div>
        </div>

        {/* Commitment Section */}
        <div className="bg-white rounded-2xl p-8 md:p-12 border border-gray-200 mb-8">
          <h2 className="text-3xl font-bold text-primary-navy mb-8 text-center">Our Commitment</h2>
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-lg text-primary-slate leading-relaxed">
              We're not just another recruitment platform — we're healthcare professionals who understand the nursing profession, 
              international migration, and the need for fair guidance. Our team blends clinical expertise with verified mobility pathways to ensure you receive accurate, up-to-date, and ethical guidance for your career.
            </p>
          </div>
        </div>

        {/* Trust Strip */}
        <div className="text-center mb-8">
          <p className="text-primary-slate opacity-80 text-lg">
            Trusted by nurses in Pakistan, India, and across the GCC.
          </p>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-primary-teal rounded-2xl p-8 md:p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">
            Start your journey today
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join 1,000+ nurses getting verified with Nursing Hive for ethical, transparent career guidance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/signup"
              className="bg-white text-primary-teal px-8 py-4 rounded-lg text-lg font-semibold hover:bg-primary-light transition-colors inline-flex items-center"
            >
              Register Free / Sign In
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/resources"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-teal transition-colors inline-flex items-center"
            >
              Browse Resources
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default About
